import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api';
import { FileQuestion, Plus, Search, ArrowRight, Trash2 } from 'lucide-react';

const fmtCurrency = (v) => `$${Number(v || 0).toFixed(2)}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';

const SalesQuotes = () => {
  const _navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchQuotes = async () => {
    try {
      const data = await api.request('/api/sales-orders?status=quote');
      setQuotes(data);
    } catch (err) {
      console.error('Failed to load quotes', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchQuotes(); }, []);

  const handleConvert = async (quoteId) => {
    if (!window.confirm('Convert this quote to a confirmed Sales Order? This will deduct inventory and add it to Account Statements.')) return;
    try {
      await api.request(`/api/sales-orders/${quoteId}/convert-quote`, { method: 'POST' });
      fetchQuotes();
    } catch (err) {
      alert('Failed to convert: ' + err.message);
    }
  };

  const handleDelete = async (quoteId) => {
    if (!window.confirm('Delete this quote?')) return;
    try {
      await api.request(`/api/sales-orders/${quoteId}`, { method: 'DELETE' });
      fetchQuotes();
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  };

  const filtered = quotes.filter(q =>
    !search || q.order_number?.toLowerCase().includes(search.toLowerCase()) ||
    q.customer_name?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="sales-quotes-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Sales Order Quotes</h1>
          <p className="text-sm text-muted-foreground mt-1">Quotes are not added to Account Statements until converted to Sales Orders</p>
        </div>
        <Link to="/sales-orders/new?status=quote"
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 text-sm font-medium"
          data-testid="create-quote-btn">
          <Plus className="h-4 w-4" /> New Quote
        </Link>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input type="text" placeholder="Search quotes..." value={search} onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-input rounded-lg bg-background text-sm"
          data-testid="search-quotes" />
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <FileQuestion className="h-12 w-12 mx-auto mb-3 opacity-40" />
          <p className="text-lg font-medium">No quotes found</p>
          <p className="text-sm">Create a new quote to get started</p>
        </div>
      ) : (
        <div className="bg-card border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/30">
                <th className="text-left px-4 py-3 font-medium">Quote #</th>
                <th className="text-left px-4 py-3 font-medium">Date</th>
                <th className="text-left px-4 py-3 font-medium">Customer</th>
                <th className="text-right px-4 py-3 font-medium">Total</th>
                <th className="text-right px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(q => (
                <tr key={q.id} className="border-b hover:bg-muted/20 transition-colors">
                  <td className="px-4 py-3">
                    <Link to={`/sales-orders/${q.id}`} className="text-primary hover:underline font-medium" data-testid={`quote-link-${q.order_number}`}>
                      {q.order_number}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{fmtDate(q.order_date || q.created_at)}</td>
                  <td className="px-4 py-3">{q.customer_name || '—'}</td>
                  <td className="px-4 py-3 text-right font-medium">{fmtCurrency(q.total)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => handleConvert(q.id)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 text-xs font-medium"
                        data-testid={`convert-quote-${q.order_number}`}>
                        <ArrowRight className="h-3 w-3" /> Convert to SO
                      </button>
                      <button onClick={() => handleDelete(q.id)}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded-md"
                        data-testid={`delete-quote-${q.order_number}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SalesQuotes;
